import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { distanceBetweenCoordsInMeters, getGeolocation } from '../../../utils/gps';
import { useKioskConfiguration } from '../../../contexts/kioskConfiguration/KioskConfigurationContext';
import { TextListTemplate } from '../textList/textListTemplate';
import {
  askPermissionForPushNotification, getPushNotificationSubscription,
  isPushNotificationSupported,
  subscribeUserToPushNotification, unsubscribeUserFromPushNotification
} from '../../../utils/notification';
import { ToggleButton } from '../../../components/toggleButton/ToggleButton';
import { getTranslationByPath } from '../../../utils/translations';
import useLanguage from '../../../contexts/language/LanguageContext';
import { isIos } from '../../../utils/userAgent';
import { setSelectedPage } from '../../../store/slices/app/appSlice';


type Block = {
  image: string
  translation: TranslationPath
}

export type InfoForDriverTemplateType = {
  transitionUrl: TransitionUrl
  infoTransitionUrl: TransitionUrl
  blocks: Block[]
  locationErrorText: string
  outOfWarehouseText: string
  infoBtnText: string
  toggleNotificationsText: string
  iosNotificationText: string
}

type Props = {
  page: Page<InfoForDriverTemplateType>
}

export const InfoForDriverTemplate = (props: Props): React.ReactElement => {
  const kioskConfiguration = useKioskConfiguration()
  const language = useLanguage()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [distance, setDistance] = useState<number | null>(null)
  const [hasLocationError, setHasLocationError] = useState<boolean>(false)
  const [isSubscribedToPushNotifications, setIsSubscribedToPushNotifications] = useState<boolean>(false)

  useEffect(() => {
    (async () => {
      try {
        if (isPushNotificationSupported()) {
          setIsSubscribedToPushNotifications((await getPushNotificationSubscription()) !== null)
        } else {
          setIsSubscribedToPushNotifications(false)
        }

        const currentGeolocation = await getGeolocation()
        setDistance(Math.ceil(distanceBetweenCoordsInMeters(
          currentGeolocation.latitude,
          currentGeolocation.longitude,
          kioskConfiguration.kioskConfiguration.warehouse.position.lat,
          kioskConfiguration.kioskConfiguration.warehouse.position.lon,
        ) / 1000))
        setIsLoading(false)
      } catch (e) {
        console.log(e)
        setHasLocationError(true)
        setIsLoading(false)
      }
    })()
  }, []);

  const handlePushNotificationToggleButton = async (checked: boolean) => {
    if (checked) {
      if (isPushNotificationSupported() && await askPermissionForPushNotification()) {
        await subscribeUserToPushNotification()
      }
    } else {
      await unsubscribeUserFromPushNotification()
    }
    setIsSubscribedToPushNotifications(await getPushNotificationSubscription() !== null)
  }

  const isInWarehouse = (): boolean => {
    return distance !== null && distance <= kioskConfiguration.kioskConfiguration.warehouse.maxDistance
  }

  let continueBtnText = undefined
  let errorText = undefined
  let showLoaderInContinueBtn = true
  let disabledContinueBtn = true
  let showContinueBtn = true
  if (!isLoading) {
    showLoaderInContinueBtn = false
    if (hasLocationError) {
      continueBtnText = props.page.data.infoBtnText
      errorText = props.page.data.locationErrorText
      disabledContinueBtn = false
    } else if (!isInWarehouse()) {
      errorText = props.page.data.outOfWarehouseText
      disabledContinueBtn = true
      showContinueBtn = false
    } else {
      disabledContinueBtn = false
    }
  }

  return <TextListTemplate page={{
    ...props.page,
    data: {
      ...props.page.data,
      continueBtnText,
      errorText,
      showLoaderInContinueBtn,
      disabledContinueBtn,
      hideContinueBtn: !showContinueBtn,
      onContinue: showContinueBtn ? () => {
        if (hasLocationError) {
          dispatch(setSelectedPage(props.page.data.infoTransitionUrl))
        } else {
          dispatch(setSelectedPage(props.page.data.transitionUrl))
        }
      } : undefined,
      textVariables: new Map([
        ['{currentKm}', (distance ?? '-').toString()],
        ['{maxKm}', (kioskConfiguration.kioskConfiguration.warehouse.maxDistance ?? '-').toString()],
      ]),
    },
  }}>
    <div className="flex items-center mb-10">
      {isPushNotificationSupported() && (
        <>
          <span className="mr-4">
            <ToggleButton checked={isSubscribedToPushNotifications} onChange={handlePushNotificationToggleButton} />
          </span>
          <span>
            {getTranslationByPath(language.translations, props.page.data.toggleNotificationsText)}
          </span>
        </>
      )}
      {!isPushNotificationSupported() && isIos() && (
        <>
          <span className="mr-4" dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.iosNotificationText) }}></span>
        </>
      )}
    </div>
  </TextListTemplate>
}
